import { graphql, useStaticQuery } from 'gatsby';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import React from 'react';

import { ReactComponent as Logo } from '../../../images/fs-logo.svg';
import ContentContainer from '../../Common/ContentContainer';
import ContentfulImage from '../../Common/ContentfulImage';
import { PrimaryLink } from '../../Common/Links';
import { NotificationBar } from '../../Common/NotificationBar';
import SmartLink from '../../Common/SmartLink';
import * as ds from './DesktopStyles.module.css';
import * as styles from './Header.module.css';
import MobileNav from './MobileNav';
import OptionalNav from './OptionalNav';
import SubMenuColumns from './SubMenuColumns';

const buildNav = (item, i, dark) => {
  const subNav = item.menuItems;
  const optionalNav = get(item, 'optionalCard', null);
  const imgLocation = get(item, 'cardImageLocation', 'Bottom');
  const link = (
    <SmartLink
      rel={item.isNoFollow ? 'nofollow' : ''}
      className={[styles.menuItemLink, ds.menuItemLink].join(' ')}
      to={item.url}
    >
      {item.title}
    </SmartLink>
  );

  const menuStyle = item.menuStyle || 'List';

  return (
    <li key={`${item.title}-${i}`} className={[styles.menuItem, ds.menuItem].join(' ')}>
      {item.url && link}
      {!item.url && menuStyle === 'Rows' && (
        <button className={[styles.menuItemLink, ds.menuItemLink].join(' ')}>
          <label>
            {item.titleUrl && (
              <SmartLink className={styles.highLevelLink} to={item.titleUrl}>
                {item.title}
              </SmartLink>
            )}
          </label>
          {subNav && (
            <div className={ds.subNav}>
              {item.titleUrl && (
                <SmartLink to={item.titleUrl} className={ds.rowColumnHead}>
                  <div className={ds.sectionTitle}>{item.sectionTitle}</div>
                  <div className={ds.sectionDesc}>{item.description ? item.description.description : ''}</div>
                </SmartLink>
              )}
              {!item.titleUrl && (
                <>
                  <div className={ds.sectionTitle}>{item.sectionTitle}</div>
                  <div className={ds.sectionDesc}>{item.description ? item.description.description : ''}</div>
                </>
              )}

              <hr />
              <div className={ds.horiList}>
                {subNav.map((sub, s) => {
                  //must be before link or button for css reasons
                  const subChild = sub.subMenuItems;

                  const sublink = (
                    <SmartLink to={sub.url} className={ds.rowColumnHead}>
                      <div className={ds.subTitle}>{sub.title}</div>
                      <div className={ds.sectionDesc}>{sub.descriptionText}</div>
                      <br />
                    </SmartLink>
                  );

                  return (
                    <li key={s} className={ds.rowColumn}>
                      {sub.url ? (
                        sublink
                      ) : (
                        <>
                          <div className={ds.subTitle}>{sub.title}</div>
                          <div className={ds.sectionDesc}>{sub.descriptionText}</div>
                          <br />
                        </>
                      )}

                      {subChild && (
                        <div className={ds.sectionCol}>
                          <ul className="unstyled-list">
                            {subChild.map((child, c) => {
                              const childlink = (
                                <div className={ds.linkRow}>
                                  <ContentfulImage className={styles.svgImg} imageFragment={child.icon} />
                                  &nbsp;&nbsp;
                                  <SmartLink
                                    to={child.url}
                                    className={[styles.menuItemLink, ds.menuItemLink, dark ? styles.darkFix : ''].join(
                                      ' '
                                    )}
                                  >
                                    {child.title}
                                    <p className={styles.itemDesc}>{child.description}</p>
                                  </SmartLink>
                                </div>
                              );

                              return (
                                <li
                                  className={[styles.menuItem, ds.menuItem, dark ? styles.darkFix : ''].join(' ')}
                                  key={c}
                                >
                                  {childlink}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </li>
                  );
                })}
                <OptionalNav optionalNav={optionalNav} imgLocation={imgLocation} />
              </div>
            </div>
          )}
        </button>
      )}
      {!item.url && item.menuStyle === 'List' && (
        <button className={[styles.menuItemLink, ds.menuItemLink].join(' ')}>
          <label>
            {item.titleUrl && (
              <SmartLink className={styles.highLevelLink} to={item.titleUrl}>
                {item.title}
              </SmartLink>
            )}
            {!item.titleUrl && item.title}
          </label>
          {subNav && (
            <div className={ds.subNav}>
              {item.titleUrl && (
                <SmartLink to={item.titleUrl} className={ds.rowColumnHead}>
                  <div className={ds.sectionTitle}>{item.sectionTitle}</div>
                  <div className={ds.sectionDesc}>{item.description ? item.description.description : ''}</div>
                </SmartLink>
              )}
              {!item.titleUrl && (
                <>
                  <div className={ds.sectionTitle}>{item.sectionTitle}</div>
                  <div className={ds.sectionDesc}>{item.description ? item.description.description : ''}</div>
                </>
              )}
              <hr />
              <div className={ds.horiList}>
                <div className={ds.sectionCol}>
                  {subNav.map((sub, s) => {
                    const childlink = (
                      <div className={ds.linkRow}>
                        <ContentfulImage className={styles.svgImg} imageFragment={sub.icon} />
                        &nbsp;&nbsp;
                        <SmartLink
                          to={sub.url}
                          className={[styles.menuItemLink, ds.menuItemLink, dark ? styles.darkFix : ''].join(' ')}
                        >
                          {sub.title}
                          <p className={styles.itemDesc}>{sub.description}</p>
                        </SmartLink>
                      </div>
                    );
                    return (
                      <li className={[styles.menuItem, ds.menuItem, dark ? styles.darkFix : ''].join(' ')} key={s}>
                        {childlink}
                      </li>
                    );
                  })}
                </div>
                <OptionalNav optionalNav={optionalNav} imgLocation={imgLocation} />
              </div>
            </div>
          )}
        </button>
      )}
      {!item.url && menuStyle === 'Stacked Columns' && (
        <button className={[styles.menuItemLink, ds.menuItemLink].join(' ')}>
          <label>
            {item.titleUrl && (
              <SmartLink className={styles.highLevelLink} to={item.titleUrl}>
                {item.title}
              </SmartLink>
            )}
            {!item.titleUrl && item.title}
          </label>
          {subNav && (
            <SubMenuColumns
              optionalNav={optionalNav}
              imgLocation={imgLocation}
              item={item}
              subNav={subNav}
              dark={dark}
            />
          )}
        </button>
      )}
    </li>
  );
};

const Header = ({
  isBlog,
  isSpark = false,
  displayCta = true,
  onCtaClick,
  hideNav = false,
  dark = false,
  className,
  pageMeta,
}) => {
  const data = useStaticQuery(graphql`
    query {
      tempNav: contentfulMenu(contentful_id: { eq: "6YBFpYDkTbMXZ3uwaLSTN" }) {
        menuItems {
          ... on ContentfulMenu {
            ...Menu
          }
          ... on ContentfulMenuItem {
            ...MenuItem
          }
        }
      }
      sparkNav: contentfulMenu(contentful_id: { eq: "9cEyZZaetJt8dt23AftGo" }) {
        menuItems {
          ... on ContentfulMenuItem {
            ...MenuItem
          }
        }
      }
      settings: contentfulSiteSettings(contentful_id: { eq: "3vxKUCou9H8hWkqL5dcaNm" }) {
        nav: headerMainNavigation {
          menuItems {
            ... on Node {
              ...Menu
            }
            ... on Node {
              ...MenuItem
            }
          }
        }
        notificationBarLabel
        notificationBarUrl
        notificationBarDisplayedAllPages
        overrideIndividualPageNotifications
        notificationBarColor
        callsToAction {
          title
          url
          isNoFollow
        }
      }
      blogSettings: contentfulBlogSettings(contentful_id: { eq: "6KdgnoVe8iL9MGRTTPgqyQ" }) {
        nav: mainNavigation {
          menuItems {
            ...MenuItem
          }
        }
        callsToAction {
          title
          url
        }
      }
    }
  `);
  const { settings, blogSettings, tempNav, sparkNav } = data;
  const marketingMenu = tempNav.menuItems.map((item, i) => buildNav(item, i, dark));
  const sparkMenu = sparkNav.menuItems.map((item, i) => (
    <li key={`${item.title}-${i}`} className={[styles.menuItem, ds.menuItem, ds.sparkItem].join(' ')}>
      <SmartLink className={[styles.menuItemLink, ds.menuItemLink].join(' ')} to={item.url}>
        {item.title}
      </SmartLink>
    </li>
  ));

  const sparkButton = (
    <li>
      <PrimaryLink
        backgroundColor="pink"
        url="/spark/register"
        isButton
        label="Register"
        className={`${ds.button} ${styles.sparkButton}`}
      />
    </li>
  );

  //const blogMenu = blogSettings.nav.menuItems.map(buildNav);

  const showBlogLogo = isBlog;

  const firstCta = isBlog ? get(blogSettings, 'callsToAction[0]') : get(settings, 'callsToAction[0]');
  const secondCta = isBlog ? get(blogSettings, 'callsToAction[1]') : get(settings, 'callsToAction[1]');

  let button = (
    <li>
      <PrimaryLink
        url={secondCta ? secondCta.url : firstCta.url}
        isButton
        label={secondCta ? secondCta.title : firstCta.title}
        className={ds.button}
      />
    </li>
  );

  let link = (
    <li>
      <SmartLink to={firstCta.url}>{firstCta.title}</SmartLink>
    </li>
  );

  const scroll = (e) => {
    e.preventDefault();
    const form = document && document.getElementById('subscribe-form');
    if (form) {
      form.scrollIntoView({
        behavior: 'smooth',
      });
    }
  };

  let blogButton = (
    <li>
      <PrimaryLink
        onClick={scroll}
        isButton
        label={secondCta ? secondCta.title : firstCta.title}
        className={ds.button}
      />
    </li>
  );

  let menu = isSpark ? sparkMenu : marketingMenu;
  if (hideNav) {
    menu = <ul />;
  }
  let override = {};
  if (process.env.GATSBY_ENV === 'development' || isSpark) {
    override.position = 'relative';
  }

  return (
    <>
      <div className={[ds.sticky, dark ? styles.darkWrap : ''].join(' ')} style={override}>
        {pageMeta && <NotificationBar meta={pageMeta} settings={settings} />}
        <ContentContainer className={[className, dark ? [styles.dark, ds.dark].join(' ') : ''].join(' ')}>
          <header className={[styles.siteHeader, ds.siteHeader].join(' ')}>
            <nav className={`${ds.siteNav} ${isSpark && ds.sparkNav}`}>
              <div role="banner" className={`${styles.logo} ${isSpark && styles.sparkLogo}`}>
                <SmartLink to="/">
                  <Logo aria-label="FullStory Logo" role="img" />
                </SmartLink>
                {showBlogLogo && (
                  <div className={styles.blogTitle}>
                    <SmartLink to="/blog">BLOG</SmartLink>
                  </div>
                )}
                {isSpark && (
                  <div className={styles.sparkTitle}>
                    <SmartLink to="/spark/">Spark</SmartLink> <span>Oct 3-5, Atlanta</span>
                  </div>
                )}
              </div>
              <ul className={[ds.mainNav, 'unstyled-list'].join(' ')}>{menu}</ul>
              {!hideNav && (
                <ul className={[styles.ctaNav, ds.ctaNav, 'unstyled-list'].join(' ')}>
                  <>
                    {secondCta && link}
                    {!isBlog && !!displayCta && button}
                    {isBlog && blogButton}
                  </>
                </ul>
              )}
              {hideNav && displayCta && (
                <ul className={[styles.ctaNav, ds.ctaNav, 'unstyled-list'].join(' ')}>
                  <li>
                    <PrimaryLink
                      isButton
                      onClick={onCtaClick}
                      label={secondCta ? secondCta.title : firstCta.title}
                      className={ds.button}
                    />
                  </li>
                </ul>
              )}
            </nav>
            {!hideNav && <MobileNav items={isSpark ? sparkNav.menuItems : tempNav.menuItems} />}
          </header>
        </ContentContainer>
      </div>
    </>
  );
};

Header.propTypes = {
  siteTitle: PropTypes.string,
};

Header.defaultProps = {
  siteTitle: ``,
};

export default Header;
